import { useCallback, useEffect, useRef, useState } from "react";
import { useGetIleCreationJobStatusLazyQuery } from "@axio/graphql/generated/graphql";
import type { MessageItem } from "../containers/companion/companionMessage.types";

type IlePollTarget = {
  streamChatKey: string;
  messageId: string | null;
};

type SetMessagesByChatId = React.Dispatch<React.SetStateAction<Record<string, MessageItem[]>>>;

export function useIleCreationPolling(args: {
  setMessagesByChatId: SetMessagesByChatId;
  hydrateCreatedILE: (createdILE: MessageItem["createdILE"] | null | undefined) => Promise<MessageItem["createdILE"]>;
  refetchActiveChat: () => Promise<unknown>;
}) {
  const { setMessagesByChatId, hydrateCreatedILE, refetchActiveChat } = args;

  const [fetchIleCreationJobStatus] = useGetIleCreationJobStatusLazyQuery();
  const ilePollTargetsRef = useRef<Record<string, IlePollTarget>>({});
  const [ilePollVersion, setIlePollVersion] = useState(0);

  const [isIleCreationInProgress, setIsIleCreationInProgress] = useState(false);
  const [ileCreationErrorMessage, setIleCreationErrorMessage] = useState<string | null>(null);

  const stopIleCreationPolling = useCallback((jobId?: string) => {
    if (jobId) {
      if (!ilePollTargetsRef.current[jobId]) return;
      const rest = { ...ilePollTargetsRef.current };
      delete rest[jobId];
      ilePollTargetsRef.current = rest;
      setIlePollVersion((prev) => prev + 1);
      if (Object.keys(rest).length === 0) {
        setIsIleCreationInProgress(false);
      }
      return;
    }
    if (Object.keys(ilePollTargetsRef.current).length === 0) return;
    ilePollTargetsRef.current = {};
    setIlePollVersion((prev) => prev + 1);
    setIsIleCreationInProgress(false);
  }, []);

  const startIleCreationPolling = useCallback((jobId: string, streamChatKey: string, aiMessageId: string | null) => {
    ilePollTargetsRef.current = {
      ...ilePollTargetsRef.current,
      [jobId]: { streamChatKey, messageId: aiMessageId },
    };
    setIlePollVersion((prev) => prev + 1);
    setIsIleCreationInProgress(true);
    setIleCreationErrorMessage(null);
  }, []);

  const remapIlePollMessageId = useCallback((oldMessageId: string, newMessageId: string) => {
    const nextTargets: Record<string, IlePollTarget> = {};
    let changed = false;
    for (const [jobId, target] of Object.entries(ilePollTargetsRef.current)) {
      if (target.messageId === oldMessageId) {
        nextTargets[jobId] = { ...target, messageId: newMessageId };
        changed = true;
      } else {
        nextTargets[jobId] = target;
      }
    }
    if (changed) {
      ilePollTargetsRef.current = nextTargets;
      setIlePollVersion((prev) => prev + 1);
    }
  }, []);

  const remapIlePollChatKey = useCallback((oldChatKey: string, newChatKey: string) => {
    const nextTargets: Record<string, IlePollTarget> = {};
    let changed = false;
    for (const [jobId, target] of Object.entries(ilePollTargetsRef.current)) {
      if (target.streamChatKey === oldChatKey) {
        nextTargets[jobId] = { ...target, streamChatKey: newChatKey };
        changed = true;
      } else {
        nextTargets[jobId] = target;
      }
    }
    if (changed) {
      ilePollTargetsRef.current = nextTargets;
      setIlePollVersion((prev) => prev + 1);
    }
  }, []);

  const failIleCreation = useCallback(
    (message: string) => {
      setIleCreationErrorMessage(message);
      setIsIleCreationInProgress(false);
      stopIleCreationPolling();
    },
    [stopIleCreationPolling]
  );

  const applyCreatedIleToMessage = useCallback(
    async (jobId: string, target: IlePollTarget, createdILE: MessageItem["createdILE"]) => {
      const hydratedILE = await hydrateCreatedILE(createdILE);

      setMessagesByChatId((prev) => {
        const chatKey = target.streamChatKey;
        const existing = prev[chatKey] ?? [];
        const fallbackMessageId =
          target.messageId ?? [...existing].reverse().find((m) => m.sender === "ai")?.id ?? null;
        if (!fallbackMessageId) return prev;
        return {
          ...prev,
          [chatKey]: existing.map((m) => (m.id === fallbackMessageId ? { ...m, createdILE: hydratedILE ?? null } : m)),
        };
      });

      setIleCreationErrorMessage(null);
      setIsIleCreationInProgress(false);
      stopIleCreationPolling(jobId);
      void refetchActiveChat();
    },
    [hydrateCreatedILE, refetchActiveChat, setMessagesByChatId, stopIleCreationPolling]
  );

  useEffect(() => {
    const pollJob = async (jobId: string, target: IlePollTarget) => {
      try {
        const { data } = await fetchIleCreationJobStatus({
          variables: { jobId },
          fetchPolicy: "network-only",
        });
        if (!ilePollTargetsRef.current[jobId]) return;
        const payload = data?.getIleCreationJobStatus;
        if (!payload) return;

        const status = payload.status;
        if (status === "in_progress") return;

        if (status === "completed" && payload.result?.id) {
          const result = payload.result;
          const createdILE: MessageItem["createdILE"] = {
            id: result.id,
            title: result.title,
            description: result.description ?? null,
            objectives: (result.objectives ?? [])
              .filter((o): o is NonNullable<typeof o> => o != null)
              .map((o) => ({
                id: o.id ?? undefined,
                objective: o.objective ?? null,
                sequence: o.sequence ?? null,
                evalCriteria: [],
              })),
          };
          await applyCreatedIleToMessage(jobId, target, createdILE);
          return;
        }

        if (status === "failed" || status === "forbidden" || status === "not_found") {
          failIleCreation(
            status === "not_found"
              ? "The activity creation job could not be found."
              : (payload.message ?? "The activity could not be created. Please try again.")
          );
          return;
        }

        failIleCreation("The activity could not be created. Please try again.");
      } catch (error) {
        console.error("[useIleCreationPolling] Error polling ILE creation job", { jobId, error });
        failIleCreation("The activity could not be created. Please try again.");
      }
    };

    const pollAllJobs = () => {
      for (const [jobId, target] of Object.entries(ilePollTargetsRef.current)) {
        void pollJob(jobId, target);
      }
    };

    if (Object.keys(ilePollTargetsRef.current).length === 0) return;
    pollAllJobs();
    const interval = setInterval(pollAllJobs, 2500);
    return () => clearInterval(interval);
  }, [applyCreatedIleToMessage, failIleCreation, fetchIleCreationJobStatus, ilePollVersion]);

  return {
    startIleCreationPolling,
    stopIleCreationPolling,
    remapIlePollMessageId,
    remapIlePollChatKey,
    failIleCreation,
    setIleCreationErrorMessage,
    isIleCreationInProgress,
    ileCreationErrorMessage,
  };
}
